import {
  Link,
  createFileRoute,
  stripSearchParams,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { useId } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import TabBar, { TabPanel, type TabBarOption } from '@/components/ui/TabBar'
import { useSuspenseCourseDetail } from '@/features/courses/api/queries'
import { unlockAction } from '@/features/courses/lib/lecture-access'
import { coursePriceView } from '@/features/courses/lib/price'
import CourseCtaBar from '@/screens/courses/components/detail/CourseCtaBar'
import CourseCtaCard from '@/screens/courses/components/detail/CourseCtaCard'
import CourseDetailSkeleton, {
  CourseCtaSkeleton,
} from '@/screens/courses/components/detail/CourseDetailSkeleton'
import CourseHead from '@/screens/courses/components/detail/CourseHead'
import {
  courseCtaRegionLabel,
  resolveCourseCta,
} from '@/screens/courses/components/detail/course-cta'
import { DETAIL_ASIDE, DETAIL_COLUMNS } from '@/screens/courses/components/detail/course-detail-box'
import CurriculumTab from '@/screens/courses/components/detail/CurriculumTab'
import DetailsTab from '@/screens/courses/components/detail/DetailsTab'
import ReviewsTab from '@/screens/courses/components/detail/ReviewsTab'
import { useCourseEnrol } from '@/screens/courses/components/detail/use-course-enrol'
import {
  COURSE_DETAIL_SEARCH_DEFAULTS,
  COURSE_TABS,
  courseDetailSearchSchema,
  courseTabSchema,
  loadCourseDetail,
  reviewsPageQuery,
  type CourseTab,
} from '@/screens/courses/CourseDetailScreen.loader'
import { LEARN_PAGE } from '@/screens/courses/components/learn/learn-page'
import LearnHead, { LearnHeadSkeleton } from '@/screens/courses/components/learn/LearnHead'
import { parseId } from '@/screens/courses/lib/params'
import { revealMotion } from '@/lib/motion/reveal'
import { cn } from '@/lib/utils'

function CourseNotFound() {
  return (
    <ScreenLayout {...LEARN_PAGE} pageTitle="Course not found">
      <EmptyState
        kind="not-found"
        size="page"
        headingLevel="h1"
        title="Course not found"
        description="This course may have been removed, or the link is wrong."
        action={
          <Link to="/courses" className={stateActionClass('page')}>
            Back to courses
          </Link>
        }
      />
    </ScreenLayout>
  )
}

function CourseDetailPending() {
  return (
    <ScreenLayout
      {...LEARN_PAGE}
      pending
      pendingLabel="Loading this course…"
      header={<LearnHeadSkeleton back sub={false} />}
    >
      <div className={DETAIL_COLUMNS}>
        <div className="flex min-w-0 flex-col gap-8">
          <CourseDetailSkeleton />
        </div>
        <div className={DETAIL_ASIDE}>
          <CourseCtaSkeleton />
        </div>
      </div>
    </ScreenLayout>
  )
}

function CourseDetailError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      fullWidth
      header={<LearnHeadSkeleton back sub={false} />}
      title="This course did not load"
    />
  )
}

export const Route = createFileRoute('/_app/courses/$courseId')({
  validateSearch: courseDetailSearchSchema,
  search: { middlewares: [stripSearchParams(COURSE_DETAIL_SEARCH_DEFAULTS)] },
  loader: loadCourseDetail,
  pendingComponent: CourseDetailPending,
  errorComponent: CourseDetailError,
  component: CourseDetailRoute,
})

/**
 * Filters a `$courseId` that cannot name a course BEFORE the suspense read
 * (the loader skipped the request for it), and keys the screen by the id so
 * moving between courses remounts it rather than carrying one course's local
 * state (open curriculum sections, the tab strip) into the next.
 */
function CourseDetailRoute() {
  const courseId = parseId(Route.useParams().courseId)
  if (courseId === null) return <CourseNotFound />
  return <CourseDetailScreen key={courseId} courseId={courseId} />
}

const TAB_LABELS: Record<CourseTab, string> = {
  lectures: 'Lectures',
  details: 'Details',
  reviews: 'Reviews',
}

const TAB_OPTIONS: readonly TabBarOption[] = COURSE_TABS.map((tab) => ({
  value: tab,
  label: TAB_LABELS[tab],
}))

/**
 * Learn · a course's own page: its level and what it is made of under the
 * title, then Lectures (the first tab — what a member opens a course for),
 * Details and Reviews.
 *
 * The CTA — owned → "Start learning"/"Continue" to the resume lecture; free
 * → "Enrol for free"; paid and on sale → "Buy this course" to its checkout
 * page; paid with no price → inert — is decided once in `resolveCourseCta`
 * and drawn twice: `CourseCtaCard`, the buy card IN the page beside the
 * course (rest:ln, owner L1: no rail on a Learn page; mock `ln-detail`), and
 * `CourseCtaBar`, an in-flow sticky bar at the end of this column wherever
 * the column is too narrow for the card (`@4xl`). Never both at once. A tab's
 * panel fades up as it opens (mock `mm-fade-up`).
 */
function CourseDetailScreen({ courseId }: { courseId: number }) {
  const idBase = useId()
  const { data: course } = useSuspenseCourseDetail(courseId)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const enrol = useCourseEnrol(courseId)

  if (course === null) return <CourseNotFound />

  const activeTab = search.tab
  const cta = resolveCourseCta(course)

  // A page number is only ever the Reviews tab's own state — leaving it set
  // while switching to any other tab would resurface a stale page (or a
  // page past its end) the moment the member switches back.
  const setTab = (value: string) => {
    const tab = courseTabSchema.parse(value)
    void navigate({
      search: (prev) => ({ ...prev, tab, page: COURSE_DETAIL_SEARCH_DEFAULTS.page }),
    })
  }

  return (
    <ScreenLayout
      {...LEARN_PAGE}
      header={<LearnHead title={course.title} back="/courses" />}
      pageTitle={course.title}
    >
      <div className={DETAIL_COLUMNS}>
        <div className="flex min-w-0 flex-col gap-8">
          <CourseHead course={course} />

          <div className="flex flex-col gap-4">
            <TabBar
              options={TAB_OPTIONS}
              value={activeTab}
              onChange={setTab}
              ariaLabel="Course sections"
              idBase={idBase}
              stripClassName="gap-8"
            />

            <TabPanel
              idBase={idBase}
              value={activeTab}
              className={cn('flex flex-col gap-6', revealMotion('fade-up').className)}
              key={activeTab}
            >
              {activeTab === 'lectures' ? (
                <CurriculumTab
                  courseId={courseId}
                  purchased={course.purchased}
                  unlock={unlockAction(coursePriceView(course))}
                  lectures={course.lectures}
                />
              ) : activeTab === 'details' ? (
                <DetailsTab course={course} />
              ) : (
                <ReviewsTab
                  courseId={courseId}
                  query={reviewsPageQuery(search)}
                  ratingAverage={course.ratingAverage}
                  ratingCount={course.ratingCount}
                  onPageChange={(page) => {
                    void navigate({ search: (prev) => ({ ...prev, page }) })
                  }}
                />
              )}
            </TabPanel>
          </div>
        </div>

        <aside className={DETAIL_ASIDE} aria-label={courseCtaRegionLabel(cta)}>
          <CourseCtaCard course={course} enrol={enrol} />
        </aside>
      </div>

      <CourseCtaBar course={course} enrol={enrol} />
    </ScreenLayout>
  )
}
